import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { checkSelectedAssets } from "./utils";
import { getAssetsById } from "./api";
import { Grid, makeStyles, Paper } from "@material-ui/core";
import { isEmpty, isEqual } from "lodash";
import { Redirect } from "react-router-dom";
import { thunks } from "../../../globalStore/slices/assets/assetsSlice";
import { useDispatch, useSelector } from "react-redux";
import Loading from "../../../components/Loading/Loading";
import Map from "./Map/Map";
import MapTable from "./Map/MapTable";
import MaterialUiButton from "../../../components/Buttons/MaterialUiButton/MaterialUiButton";
import ModalDialog from "../../../components/Modals/ModalDialog/ModalDialog";
import MuiAlert from "@material-ui/lab/Alert";
import Snackbar from "@material-ui/core/Snackbar";

// Components are super heavy, which is why we are using lazy/Suspense
const ImportAssets = lazy(() => import("../ImportAssets/ImportAssets"));
const AssignDevice = lazy(() => import("../assignDevice/AssignDevice"));
const Table = lazy(() => import("./Table/Table"));

const useStyles = makeStyles((theme) => ({
  button: {
    marginLeft: "1rem",
    marginBottom: ".25rem",
  },
  buttonContainer: {
    textAlign: "right",
  },
  paper: {
    padding: theme.spacing(2),
    textAlign: "center",
    minHeight: "64vh",
    maxHeight: "64vh",
  },
  map: {
    padding: theme.spacing(2),
    minHeight: "62vh",
    maxHeight: "62vh",
  },
  mapTable: {
    padding: theme.spacing(2),
    minHeight: "62vh",
    maxHeight: "62vh",
    overflow: "auto",
  },
}));

export default function AssetStatus(props) {
  const dispatchGlobal = useDispatch();
  const saveFilterSettings = thunks.assets.saveFilterSettings;
  const showLastEvents = thunks.assets.showLastEvents;

  const classes = useStyles();
  const {
    apiUrl,
    appUserType,
    organizationId,
    timeZone,
    token,
    userId,
    usersConsoleRole,
  } = props;

  // OK to destructure, as no changes to org data takes place in this page
  const {
    organization,
    zones,
    facilities,
    customs,
    devices,
    classifications,
    usersMap,
    products,
  } = useSelector((state) => state.organization, isEqual);

  let assetStatusClassifications = {}
  
  Object.keys(classifications).forEach(key => {
    let classification = classifications[key]
    if(classification.assetMode === "Asset" || !classification.assetMode) { assetStatusClassifications = {...assetStatusClassifications, [key]: classification} }
  });  

  const assetTypes =
    organization && organization.assetTypes
      ? [...organization.assetTypes].sort()
      : [];

  const eventTypes =
    organization && organization.eventTypesMap
      ? Object.keys(organization.eventTypesMap).sort()
      : [];

  const facilityArray = facilities
    ? Object.values(facilities).sort((a, b) => {
        return (a.name || "").localeCompare(b.name || "");
      })
    : [];

  const allDevices = Object.keys(devices).map((k) => devices[k]);
  const availableDevices = allDevices.filter(
    (item) => item.device.status !== "assigned"
  );

  const assetsStore = useSelector((state) => state.assets, isEqual);

  const { filters: initFilters = {} } = assetsStore;

  // make sure dates are formatted correctly for our datepicker components
  const startDate = initFilters.startDate
    ? new Date(initFilters.startDate)
    : null;
  const endDate = initFilters.endDate ? new Date(initFilters.endDate) : null;

  const [state, setState] = useState({
    count: assetsStore.count,
    filters: { ...initFilters, startDate, endDate },
    lists: {
      assetTypes: assetTypes,
      eventTypes: eventTypes,
      eventTypesMap: organization.eventTypesMap || {},
      facilityArray: facilityArray,
      assetCategoriesList:
        organization.assetCategoriesList || organization.assetCategories || [],
    },
    selectedAssets: {},
    allDevices,
    availableDevices,
    availableZones: Object.keys(zones).map((zone) => {
      // TODO: Is this how we are going to handle the differences between
      // pZones and tZones?
      return {
        internalZoneType: zones[zone].internalZoneType || null,
        label: zones[zone].name,
        value: zones[zone].zoneId,
      };
    }),
    mapTableData: [],
    page: 0,
    mounted: true,
  });

  const [loading, setLoading] = useState(false);

  const [modal, setModal] = useState({
    modalShow: false,
    title: "",
    content: {},
  });

  const modalClose = () =>
    setModal({ modalShow: false, title: "", content: "" });

  const [dialog, setDialog] = useState({
    dialogShow: false,
  });

  const [isRedirect, setIsRedirect] = useState(false);

  const [confirmationModalShow, setConfirmationModalShow] = useState({
    modalShow: false,
    text: "",
    error: false,
  });

  const [importAssetsModalTitle, setImportAssetsModalTitle] =
    useState("Import Assets");

  const isMountedRef = useRef(false);

  // upon first mount, this function will check for updates in the store by dispatching the showLastEvents thunk
  const init = useCallback(
    (isMounted) => {
      if (!isMounted) {
        dispatchGlobal(
          showLastEvents({ filters: state.filters, page: state.page })
        ).then((res) => {
          isMountedRef.current = true;
          setState((prevState) => ({
            ...prevState,
            assets: res.payload.assets,
          }));
        });
      }
    },
    [dispatchGlobal, showLastEvents, state]
  );

  // intializing effect, this is for checking for updated asset status on first mount using initial filters. Using a ref to ensure it only fires on first mount.
  useEffect(() => {
    init(isMountedRef.current);
  }, [init]);

  // this effect fires whenever filters are updated (either in the filter menu or by changing the page or row limit).
  // it checks the isMountedRef to prevent firing on first mount, sets loading to true, and then dispatches the showLastEvents thunk to our store to searchAssets and update state
  // it then takes the return from our showLastEvents thunk and updates the local state
  useEffect(() => {
    if (isMountedRef.current) {
      setLoading(true);
      dispatchGlobal(
        showLastEvents({ filters: state.filters, page: state.page })
      ).then((results) => {
        setLoading(false);
        setState((prevState) => {
          return {
            ...prevState,
            assets: results.payload.assets,
            count: results.payload.count,
            page: Math.floor(state.filters.start / state.filters.limit),
            // reset map table data
            mapTableData: [],
          };
        });
      });
    }
  }, [dispatchGlobal, showLastEvents, state.filters, state.page]);

  const handleSave = async (filters) => {
    // Removes the start from saving to the users settings
    delete filters.start;

    return await dispatchGlobal(saveFilterSettings(filters));
  };

  async function checkDevices() {
    let isValid = false;
    let assets;
    let assetIds = [
      ...new Set(
        Object.keys(state.selectedAssets).map((asset) => {
          return asset;
        })
      ),
    ];

    // Retrieve up to date asset information
    assets = await getAssetsById(
      { apiUrl, token, organizationId },
      assetIds,
      1000
    );

    // Checks to verify selected asset(s) do not currently have a device associated
    const hasParentId = assets.assets.find((element) => element.parentId);

    // Checks to verify they actually have enough devices for the amount of selected assets
    const hasEnoughDevices =
      state.availableDevices.length < Object.keys(state.selectedAssets).length;

    if (hasEnoughDevices) {
      setConfirmationModalShow({
        modalShow: true,
        text: "You have selected more assets than there are available devices. Please adjust your selections.",
        error: true,
      });
    } else if (hasParentId) {
      setConfirmationModalShow({
        modalShow: true,
        text: "You have selected one or more assets that already has a device assigned to it.",
        error: true,
      });
    } else isValid = true;

    if (isValid) {
      setModal({
        modalShow: true,
        title: "Assign a Device",
        content: (
          <Suspense fallback={<Loading />}>
            <AssignDevice
              allDevices={state.allDevices}
              availableDevices={state.availableDevices}
              apiUrl={apiUrl}
              token={token}
              selectedAssets={state.selectedAssets}
              setModal={setModal}
              setConfirmationModalShow={(value) => {
                setConfirmationModalShow(value);
                setTimeout(() => {
                  setConfirmationModalShow({
                    modalShow: false,
                    text: "",
                    error: false,
                  });
                }, 1000);
              }}
              timezone={timeZone}
            />
          </Suspense>
        ),
      });
    }
  }

  return (
    <Grid
      className="asset-history"
      container
      style={{
        marginTop: "1rem",
        width: "100% !important",
        padding: "0 15px",
        margin: "0 auto",
      }}
    >
      {isRedirect ? (
        <Redirect
          to={{
            pathname: "/generateCQR/",
            state: { selectedAssets: state.selectedAssets },
          }}
        />
      ) : (
        ""
      )}
      <ModalDialog
        handleClose={modalClose}
        open={modal.modalShow}
        title={modal.title}
        content={
          <Grid container>
            <Grid item xs={12}>
              {modal.content}
            </Grid>
          </Grid>
        }
      />
      <ModalDialog
        handleClose={() => {
          setDialog({
            dialogShow: false,
          });
        }}
        open={dialog.dialogShow}
        title={importAssetsModalTitle}
        content={
          <Suspense fallback={<Loading />}>
            <ImportAssets
              apiUrl={apiUrl}
              availableDevices={state.availableDevices}
              classifications={assetStatusClassifications || {}}
              facilities={facilities}
              onHide={() => {
                setDialog({
                  dialogShow: false,
                });
                setImportAssetsModalTitle("Import Assets");
              }}
              onSuccess={() => {
                // force refetch via useEffect
                setState({
                  ...state,
                  filters: {
                    ...state.filters,
                  },
                });
              }}
              organization={organization}
              setConfirm={setConfirmationModalShow}
              setImportAssetsModalTitle={setImportAssetsModalTitle}
              token={token}
            />
          </Suspense>
        }
      />

      {/* My assumption is that we are going to need to change the material confirmation modal to match the snack bar but for now ill set it here */}
      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        open={confirmationModalShow.modalShow}
        onClose={() => {
          setConfirmationModalShow((prevState) => ({
            ...prevState,
            modalShow: false,
          }));
        }}
      >
        <MuiAlert
          onClose={() => {
            setConfirmationModalShow((prevState) => ({
              ...prevState,
              modalShow: false,
            }));
          }}
          severity={confirmationModalShow.error ? "error" : "success"}
        >
          {confirmationModalShow.text}
        </MuiAlert>
      </Snackbar>
      <Grid
        item
        xs={12}
        style={{
          marginBottom: "1rem",
          marginTop: "1.5rem",
        }}
      >
        <Grid
          item
          xs={12}
          style={{
            textAlign: "left",
          }}
        >
          <h3>Asset Status</h3>
        </Grid>
        <Grid
          item
          xs={12}
          style={{
            textAlign: "right",
          }}
        >
          {/* View Assets on Map Button */}
          <MaterialUiButton
            className={classes.button}
            label={
              state.displayMap ? `View Assets on Table` : `View Assets on Map`
            }
            onClick={() => {
              setState((s) => {
                return {
                  ...s,
                  displayMap: !s.displayMap,
                };
              });
            }}
            size="small"
          />

          {/* Lite Users are unable to manipulate data */}
          {usersConsoleRole === "Lite" || state.displayMap ? null : (
            <>
              {/* Assign a Device */}
              <MaterialUiButton
                className={classes.button}
                disabled={isEmpty(state.selectedAssets)}
                label="Assign a Device"
                onClick={() => {
                  if (!isEmpty(state.selectedAssets)) {
                    checkDevices();
                  }
                }}
                size="small"
              />

              {/* Generate CQRs Button */}
              <MaterialUiButton
                className={classes.button}
                disabled={isEmpty(state.selectedAssets)}
                label="Generate CQRs"
                onClick={() => {
                  if (!isEmpty(state.selectedAssets)) {
                    checkSelectedAssets(
                      { organizationId, apiUrl, token },
                      state
                    ).then((results) => {
                      const { faultyContent = [], isError = true } = results;
                      if (!isError) {
                        setIsRedirect(true);
                      } else {
                        setConfirmationModalShow({
                          modalShow: true,
                          text: `The Asset Tag(s)${faultyContent.map((item) => {
                            return ` ${item}`;
                          })} are shown as still being in circulation, therefore the CQR cannot be regenerated. If you need to reprint a CQR for an asset, please contact your organizations Admin User.`,
                          error: true,
                        });
                      }
                    });
                  }
                }}
                size="small"
              />

              {/* Import Assets Button */}
              <MaterialUiButton
                className={classes.button}
                label="Import Assets"
                onClick={() => {
                  setDialog({ dialogShow: true });
                }}
                size="small"
              />
            </>
          )}
        </Grid>
      </Grid>
      <Grid item xs={12}>
        {state.filters ? (
          <>
            {state.displayMap ? (
              <>
                <Paper className={classes.paper}>
                  <Grid container spacing={3}>
                    <Grid item sm={6} xs={12} className={classes.map}>
                      <Map
                        apiUrl={apiUrl}
                        classifications={assetStatusClassifications}
                        dispatchGlobal={dispatchGlobal}
                        eventTypesMap={state.lists.eventTypesMap}
                        facilities={facilities}
                        organizationId={organizationId}
                        setConfirm={setConfirmationModalShow}
                        setLoading={setLoading}
                        setState={setState}
                        showLastEvents={showLastEvents}
                        state={state}
                        timeZone={timeZone}
                        token={token}
                      />
                    </Grid>
                    <Grid item sm={6} xs={12} className={classes.mapTable}>
                      <p>
                        Hover over marker and click "See Data" to view multiple
                        assets from the same location in the table below.
                      </p>
                      <MapTable
                        mapTableData={state.mapTableData}
                        timeZone={state.filters.tz ? state.filters.tz : ""}
                        facilities={facilities}
                        zones={zones}
                        setState={setState}
                      />
                    </Grid>
                  </Grid>
                </Paper>
              </>
            ) : (
              <Suspense fallback={<Loading />}>
                <Table
                  allDevices={state.allDevices}
                  apiUrl={apiUrl}
                  appUserType={appUserType}
                  classifications={assetStatusClassifications}
                  customs={customs}
                  defaultColumnOrder={state.filters?.defaultColumnOrder}
                  dispatchGlobal={dispatchGlobal}
                  facilities={facilities}
                  handleSave={handleSave}
                  loading={loading}
                  organizationId={organizationId}
                  page={state.page}
                  products={products}
                  setConfirm={setConfirmationModalShow}
                  setLoading={setLoading}
                  setModal={setModal}
                  setState={setState}
                  showLastEvents={showLastEvents}
                  state={state}
                  timeZone={timeZone}
                  token={token}
                  userId={userId}
                  usersMap={usersMap}
                  zones={zones}
                />
              </Suspense>
            )}
          </>
        ) : null}
      </Grid>
    </Grid>
  );
}
